import React from 'react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Your privacy is our priority — we do not store your analysis data
          </p>
        </div>

        {/* Content */}
        <div className="bg-slate-900/85 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-6 sm:p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Introduction</h2>
              <p className="text-slate-300 leading-relaxed">
                At EmotionWise, we are committed to protecting your privacy. We do not store any text you submit for analysis or any analysis results.
                Your messages are processed in real time and discarded immediately after the response is returned. This Privacy Policy explains how we handle your information when you use our emotion detection service.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside text-slate-300 space-y-1 ml-4">
                    <li>Email address (for account creation and authentication)</li>
                    <li>Password (encrypted and securely stored)</li>
                    <li>Account preferences and settings</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-200 mb-2">Analysis Data — Not Stored</h3>
                  <p className="text-slate-300">
                    We do not store any text you submit for emotion analysis, nor do we store the analysis results (emotions, confidence scores, or sarcasm signals).
                    All processing happens in real time and data is discarded immediately after the API response is returned. No analysis history is retained on our servers.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-2">Technical Information</h3>
                  <ul className="list-disc list-inside text-slate-300 space-y-1 ml-4">
                    <li>IP address (used for rate limiting and abuse prevention)</li>
                    <li>Request counts and timestamps (for quota enforcement only — no message content)</li>
                  </ul>
                  <p className="text-slate-400 text-sm mt-1">We do not collect browser type, device information, or operating system data.</p>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">How We Use Your Information</h2>
              <div className="space-y-4">
                <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-indigo-200 mb-2">Service Provision</h3>
                  <p className="text-indigo-200">
                    To provide emotion detection services, process your requests, and deliver accurate results.
                  </p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-emerald-200 mb-2">Account Management</h3>
                  <p className="text-emerald-200">
                    To create and manage your account, authenticate your identity, and provide customer support.
                  </p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-purple-200 mb-2">Service Improvement</h3>
                  <p className="text-purple-200">
                    To improve our AI models and develop new features based on aggregated, anonymous usage patterns. We do not store or use your submitted text or analysis results for model training.
                  </p>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-orange-200 mb-2">Communication</h3>
                  <p className="text-orange-200">
                    To send important updates, security notifications, and respond to your inquiries.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Data Security</h2>
              <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-200 mb-3">Encryption</h3>
                    <p className="text-slate-300">
                      All data is encrypted in transit using TLS/SSL protocols and at rest using industry-standard encryption.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-200 mb-3">Access Control</h3>
                    <p className="text-slate-300">
                      Strict access controls and authentication mechanisms protect your data from unauthorized access.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-200 mb-3">Security Reviews</h3>
                    <p className="text-slate-300">
                      We review security practices and infrastructure configuration as part of ongoing development.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-200 mb-3">Zero Analysis Storage</h3>
                    <p className="text-slate-300">
                      We do not store any user-submitted text or analysis results. Your messages are processed in real time and immediately discarded.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Cookies and Tracking Technologies */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-slate-300 leading-relaxed">
                We use session cookies required for authentication. We do not currently use Google Analytics, session replay, or third-party advertising trackers. If we add analytics tools in the future, we will update this policy and provide opt-out options.
              </p>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Data Sharing</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share data only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our platform (hosting, analytics, etc.)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Consent:</strong> With your explicit consent for specific purposes</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Your Rights</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-indigo-200 mb-2">No Analysis Data Stored</h3>
                  <p className="text-indigo-200">
                    Since we do not store any text you submit or any analysis results, there is no analysis data to access, export, or delete.
                  </p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-red-200 mb-2">Account Deletion</h3>
                  <p className="text-red-200">
                    You can request deletion of your account and associated account data (email, API keys) at any time.
                  </p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-amber-200 mb-2">Correction</h3>
                  <p className="text-amber-200">
                    You can update or correct your personal information through your account settings.
                  </p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-emerald-200 mb-2">Opt-out</h3>
                  <p className="text-emerald-200">
                    You can opt out of non-essential communications by emailing support@emotionwise.ai.
                  </p>
                </div>
              </div>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Children's Privacy</h2>
              <p className="text-slate-300 leading-relaxed">
                Our service is not intended for children under the age of 13. We do not knowingly collect personal data from children without verifiable parental consent. If you believe we have collected such data, please contact us immediately at support@emotionwise.ai.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Contact Us</h2>
              <div className="bg-slate-800/80 border border-slate-600 rounded-xl p-6">
                <p className="text-slate-300 mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-3 text-slate-300">
                  <div>
                    <strong className="text-slate-100">Privacy Inquiries:</strong>{' '}
                    <a 
                      href="mailto:support@emotionwise.ai"
                      className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                    >
                      support@emotionwise.ai
                    </a>
                  </div>
                  <div>
                    <strong className="text-slate-100">General Support:</strong>{' '}
                    <a 
                      href="mailto:support@emotionwise.ai"
                      className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                    >
                      support@emotionwise.ai
                    </a>
                  </div>
                  <p className="text-sm text-slate-400 mt-3">
                    <strong>Response Time:</strong> We aim to respond to all privacy inquiries within 48 hours.
                  </p>
                </div>
                <div className="mt-4 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <p className="text-indigo-200 text-sm">
                    For any issues, technical problems, or general feedback, you can email us at{' '}
                    <a 
                      href="mailto:support@emotionwise.ai"
                      className="text-indigo-300 hover:text-indigo-200 hover:underline font-medium"
                    >
                      support@emotionwise.ai
                    </a>.
                  </p>
                </div>
              </div>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Updates to This Policy</h2>
              <p className="text-slate-300 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. 
                We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date.
              </p>
            </section>

            {/* Privacy Contact Section */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Privacy Support</h2>
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-6">
                <p className="text-indigo-200 mb-3">
                  For privacy-related concerns or data protection questions:
                </p>
                <div className="flex items-center space-x-2">
                  <span className="text-indigo-200 font-medium">Email us at:</span>
                  <a 
                    href="mailto:support@emotionwise.ai" 
                    className="text-indigo-200 font-mono bg-indigo-500/100/20 px-2 py-1 rounded hover:underline hover:bg-indigo-500/100/30 transition-colors duration-200"
                  >
                    support@emotionwise.ai
                  </a>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="border-t border-slate-700 pt-6 text-center">
              <p className="text-sm text-slate-500 mb-4">
                Last Updated: March 6, 2026
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/terms"
                  className="text-sm text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 